import {computed, inject, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {defer, merge, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {dependOn} from '../../../../common/rxjs/depend-on';
import {DexStreamingService} from '../dex-streaming.service';
import {DexMdSubjectService} from '../subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../subjects/dex-securities-subject-service';
import {ComputedHeldPosition} from '../types/computed/computed-held-position';
import {ComputedLp} from '../types/computed/computed-lp';
import {ComputedTranche} from '../types/computed/computed-tranche';
import {Deliverable} from '../types/deliverable';
import {HeldPosition} from '../types/held-position';
import {LiquidityPool} from '../types/liquidity-pool';
import {DexComputedHeldPositionService} from './dex-computed-held-position.service';
import {DexComputedTradableService} from './dex-computed-tradable.service';
import {DexComputedTrancheService} from './dex-computed-tranche.service';

@Injectable()
export class DexComputedLpService {

    public readonly list$: Observable<ComputedLp[]>;
    public readonly $list: Signal<ComputedLp[]>;

    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly dexMdSubjectService = inject(DexMdSubjectService);
    private readonly dexStreamingService = inject(DexStreamingService);
    private readonly dexComputedHeldPositionService = inject(DexComputedHeldPositionService);
    private readonly dexComputedTrancheService = inject(DexComputedTrancheService);
    private readonly dexComputedTradableService = inject(DexComputedTradableService);

    private readonly $dependenciesUpdated = toSignal(merge(
        this.dexSecuritiesSubjectService.deliverablesUpdated$,
        this.dexSecuritiesSubjectService.tradablesUpdated$,
        this.dexMdSubjectService.positionsUpdated$,
        this.dexMdSubjectService.tranchesUpdated$,
        this.dexMdSubjectService.lpUpdated$,
        this.dexComputedTrancheService.list$
    ), {equal: () => false});

    constructor() {
        this.list$ = this.createListStream();
        this.$list = toSignal(this.list$, {initialValue: []});
    }

    public create(lp: LiquidityPool): ComputedLp {
        const unitDeliverable = this.dexSecuritiesSubjectService.getDeliverableById(lp.unitDeliverableId);
        const unitPriceDeliverable = this.dexSecuritiesSubjectService.getDeliverableById(lp.unitPriceDeliverableId);
        const unitTradeable = this.dexComputedTradableService.createById(lp.unitTradeableId);

        const $calc = computed(() => {
            this.$dependenciesUpdated();

            const {positions, computedPositions} = this.getLpPositions(lp);

            const {computedTranches, totalValueLocked, totalValueLockedDeliverable} = this.getLpTranches(lp);
            const portfolioValues = this.calcPortfolioValue(totalValueLocked, computedPositions);

            return {
                positions,
                computedPositions,
                computedTranches,
                totalValueLocked,
                totalValueLockedDeliverable,
                ...portfolioValues
            };
        });

        return {
            raw:                          lp,
            unitDeliverable,
            unitPriceDeliverable,
            unitTradeable,
            $computedPositions:           computed(() => $calc()?.computedPositions),
            $computedTranches:            computed(() => $calc()?.computedTranches),
            $totalValueLocked:            computed(() => $calc()?.totalValueLocked),
            $totalValueLockedDeliverable: computed(() => $calc()?.totalValueLockedDeliverable),
            $portfolioValue:              computed(() => $calc()?.portfolioValue),
            $portfolioValueDeliverable:   computed(() => $calc()?.portfolioValueDeliverable),
            $uPnlPercent:                 computed(() => $calc()?.uPnlPercent),
            $uPnlNumber:                  computed(() => $calc()?.uPnlNumber)
        };
    }

    private createListStream(): Observable<ComputedLp[]> {
        const build = () => this.buildList();

        return merge(
            defer(() => build()),
            this.dexSecuritiesSubjectService.deliverablesUpdated$,
            this.dexSecuritiesSubjectService.tradablesUpdated$,
            this.dexMdSubjectService.tranchesUpdated$,
            this.dexMdSubjectService.lpUpdated$
        ).pipe(
            map(build),
            dependOn(this.dexStreamingService.stream$)
        );
    }

    private buildList(): ComputedLp[] {
        const list: ComputedLp[] = [];

        this.dexMdSubjectService.lpMapById.forEach(lp => {
            list.push(this.create(lp));
        });

        // TODO: debug
        (window as any).dexLpList = list;

        return list;
    }

    private getLpPositions(lp: LiquidityPool): {
        positions: HeldPosition[];
        computedPositions: ComputedHeldPosition[];
    } {
        const result: {
            positions: HeldPosition[];
            computedPositions: ComputedHeldPosition[];
        } = {
            positions: [],
            computedPositions: []
        };

        const posMap = this.dexMdSubjectService.posMapByAccountId.get(lp.reservedAccountId);

        if (posMap) {
            for (const pos of posMap.values()) {
                result.computedPositions.push(this.dexComputedHeldPositionService.create(pos));
                result.positions.push(pos);
            }
        }

        return result;
    }

    private getLpTranches(lp: LiquidityPool): {
        computedTranches: ComputedTranche[];
        totalValueLocked: bigint;
        totalValueLockedDeliverable: Deliverable;
    } {
        const computedTranches: ComputedTranche[] = [];
        let totalValueLocked: bigint = 0n;
        let totalValueLockedDeliverable: Deliverable = undefined;

        for (const tranche of this.dexComputedTrancheService.$list()) {
            if (tranche.raw.liquidityPoolId === lp.liquidityPoolId) {
                computedTranches.push(tranche);

                if (tranche.$totalValue()) {
                    totalValueLocked += tranche.$totalValue();
                }

                totalValueLockedDeliverable ??= tranche.$totalValueDeliverable();
            }
        }

        return {
            computedTranches,
            totalValueLocked,
            totalValueLockedDeliverable
        };
    }

    private calcPortfolioValue(
        totalValueLocked: bigint | undefined,
        computedPositions: ComputedHeldPosition[]
    ): {
        uPnlPercent: number;
        uPnl: bigint | undefined;
        uPnlNumber: number | undefined;
        portfolioValue: bigint | undefined;
        portfolioValueDeliverable: Deliverable | undefined;
    } {
        let uPnl: bigint | undefined;
        let uPnlSum: bigint = 0n;
        let uPnlPercentSum: number = 0;
        let portfolioValueDeliverable: Deliverable | undefined = undefined;

        if (computedPositions.length > 0) {
            let pos = computedPositions[0];
            uPnlPercentSum = pos.yieldPercent || 0;
            uPnl = pos.profitOrLoss.v || 0n;
            uPnlSum = uPnl;
            portfolioValueDeliverable = pos.valueDeliverable;

            for (let i = 1; i < computedPositions.length - 1; i++) {
                pos = computedPositions[i];
                uPnlPercentSum = uPnlPercentSum + (pos.yieldPercent || 0);
                uPnlSum += (pos.profitOrLoss.v || 0n);
            }
        }

        const uPnlPercent = computedPositions.length > 0
            ? Math.round(uPnlPercentSum * 100 / computedPositions.length) / 100
            : 0;

        const portfolioValue = (totalValueLocked || 0n) + uPnlSum;

        return {
            uPnlPercent,
            uPnl,
            uPnlNumber: uPnl !== undefined ? Number(uPnl) : undefined,
            portfolioValue,
            portfolioValueDeliverable
        };
    }

}
